import React from "react";

import { useMatch, useNavigate } from "@tanstack/react-location";

import { useUserAccounts } from "../UserProvider";
import * as routeHelpers from "../../router/routeHelpers";
import { useToast } from "../ToastProvider";
import { AccountProvider } from "./AccountProvider";

type RouteAwareAccountProviderProps = {
  paramName: string;
};

/**
 * Wrapper around the the AccountProvider which connects the url with
 * the current account. It takes a slug and resolves the associated account id
 * from the user's accounts (already loaded by UserProvider) and passes it to
 * the AccountProvider.
 *
 * @param param0
 * @returns
 */
export function RouteAwareAccountProvider({
  paramName,
  children,
}: React.PropsWithChildren<RouteAwareAccountProviderProps>) {
  const { params } = useMatch();
  const slug = params[paramName];
  const navigate = useNavigate();
  const toast = useToast();

  const accounts = useUserAccounts();
  const accountId = accounts.find((x) => x.slug === slug)?.id;

  const navigateToWorkspaces = React.useCallback(() => {
    navigate({
      to: routeHelpers.workspacesRoute(),
    });
  }, [navigate]);

  React.useEffect(() => {
    if (accountId !== undefined) return;
    toast(
      "Workspace not found",
      `Workspace with slug "${slug}" not found`,
      "error",
    );
    navigateToWorkspaces();
  }, [accountId, slug, toast, navigateToWorkspaces]);

  const handleInvalidAccount = React.useCallback(async () => {
    navigateToWorkspaces();
  }, [navigateToWorkspaces]);

  if (accountId === undefined) {
    return null;
  }

  return (
    <AccountProvider
      accountId={accountId}
      onInvalidAccount={handleInvalidAccount}
    >
      {children}
    </AccountProvider>
  );
}
